import { inject, Injectable } from '@angular/core';
import {
  collection,
  doc,
  Firestore,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  Unsubscribe,
  where
} from '@angular/fire/firestore';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { BehaviorSubject } from 'rxjs';
import { GimOrder } from '../models/gim-order.model';
import { GIM_PRICE_LIST_ID, GimSettings } from '../models/settings-gim.model';
import { GimSyncStatus } from '../models/gim-sync-status.model';

/** Multipress product of the JCDecaux price grid that can be mapped */
export interface GimMultipressProduct {
  code: string;
  /** Multipress standard number */
  standardId: number;
}

@Injectable({
  providedIn: 'root',
})
export class GimService {
  private unsubscribeOrders: Unsubscribe;
  private orders$: BehaviorSubject<GimOrder[]>;
  private unsubscribeSyncStatus: Unsubscribe;
  private syncStatus$: BehaviorSubject<GimSyncStatus | null>;
  private syncStatusOrganizationId: string;
  private organizationId: string;
  private firestore = inject(Firestore);
  private functions = inject(Functions);

  /**
   * Get the synchronized GIM orders sorted by import date desc
   * @param organizationId The organization id
   * @returns The orders
   */
  public getOrders(organizationId: string): BehaviorSubject<GimOrder[]> {
    if (this.orders$ && this.organizationId !== organizationId) {
      this.unsubscribe();
    }
    if (!this.orders$) {
      this.organizationId = organizationId;
      this.orders$ = new BehaviorSubject<GimOrder[]>([]);
      const q = query(
        collection(this.firestore, 'gim-orders'),
        where('organizationId', '==', organizationId),
        orderBy('date', 'desc'))
        .withConverter(GimOrder.converter);
      this.unsubscribeOrders = onSnapshot(q, snapshot => {
        this.orders$.next(snapshot.docs.map(doc => doc.data()));
      }, error => console.error(error));
    }
    return this.orders$;
  }

  /**
   * Result of the last GIM synchronization
   * @param organizationId The organization id
   * @returns The status, null until the first run
   */
  public getSyncStatus(organizationId: string): BehaviorSubject<GimSyncStatus | null> {
    if (!this.syncStatus$ || this.syncStatusOrganizationId !== organizationId) {
      this.syncStatusOrganizationId = organizationId;
      this.unsubscribeSyncStatus?.();
      this.syncStatus$ = new BehaviorSubject<GimSyncStatus | null>(null);
      this.unsubscribeSyncStatus = onSnapshot(doc(this.firestore, `settings/${organizationId}/gim/status`),
        snapshot => this.syncStatus$.next(snapshot.exists() ? new GimSyncStatus(snapshot.data()) : null),
        error => console.error(error));
    }
    return this.syncStatus$;
  }

  /**
   * Force the GIM synchronization of the organization, even when the automatic one is disabled
   * @param organizationId The organization id
   * @returns The import summary
   */
  async syncNow(organizationId: string): Promise<{ total: number, created: number, skipped: number }> {
    return httpsCallable<{ organizationId: string }, { total: number, created: number, skipped: number }>(
      this.functions, 'sync_gim_orders_now_gen2')({ organizationId }).then(res => res.data);
  }

  /**
   * Create an order in Multipress, or retry its creation after a failure
   * @param orderId The GIM order id
   * @returns void
   */
  async retry(orderId: string): Promise<{ batContactPending: boolean }> {
    return httpsCallable<{ orderId: string }, { batContactPending?: boolean }>(this.functions, 'retry_gim_order_gen2')({ orderId })
      .then(res => ({ batContactPending: !!res.data?.batContactPending }));
  }

  /**
   * Archive a GIM order, which is no longer displayed in the orders list
   * @param orderId The GIM order id
   * @returns void
   */
  async archive(orderId: string): Promise<void> {
    return httpsCallable<{ orderId: string }, void>(this.functions, 'archive_gim_order_gen2')({ orderId })
      .then(res => res.data);
  }

  /**
   * Put an archived GIM order back to À traiter
   * @param orderId The GIM order id
   * @returns void
   */
  async unarchive(orderId: string): Promise<void> {
    return httpsCallable<{ orderId: string }, void>(this.functions, 'unarchive_gim_order_gen2')({ orderId })
      .then(res => res.data);
  }

  /**
   * Get the GIM settings
   * @param organizationId The organization id
   * @returns The settings, default settings if none are saved
   */
  async getSettings(organizationId: string): Promise<GimSettings> {
    const newSettings = new GimSettings();
    const ref = doc(this.firestore, `settings/${organizationId}/${newSettings.type}/data`)
      .withConverter(newSettings.converter);
    const res = await getDoc(ref);
    return res.exists() ? res.data() : newSettings;
  }

  /**
   * Get the Multipress products of the JCDecaux price grid (99998) that can be mapped:
   * the ones based on a standard, without autofill (the dimensions of an autofill product would be needed)
   * @returns The product codes and standard numbers sorted by code
   */
  async getMultipressProducts(): Promise<GimMultipressProduct[]> {
    const snapshot = await getDocs(query(collection(this.firestore, 'mp-variation'), orderBy('code', 'asc')));
    return snapshot.docs
      .map(doc => doc.data())
      .map(variation => ({
        code: variation['code'] as string,
        standardId: ((variation['parameters'] ?? []) as { customerId: number, standardId: number, autofill?: unknown }[])
          .find(parameter => parameter.customerId === GIM_PRICE_LIST_ID && !parameter.autofill)?.standardId
      }))
      .filter((product): product is GimMultipressProduct => product.standardId !== undefined);
  }

  /**
   * Stop listening to the orders
   */
  unsubscribe(): void {
    if (this.unsubscribeSyncStatus) {
      this.unsubscribeSyncStatus();
      this.unsubscribeSyncStatus = null;
      this.syncStatus$ = null;
    }
    if (this.unsubscribeOrders) {
      this.unsubscribeOrders();
      this.unsubscribeOrders = null;
      this.orders$ = null;
    }
  }
}
